import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { loadGoogleMaps, attachAutocomplete, detachAutocomplete, MAPS_AUTH_EVENT } from "@/lib/googleMaps";
import { ForwardingNote, EMPTY_FORWARDING_NOTE } from "@/flows/ForwardingNote";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, CheckCircle2, Package, Ruler, CreditCard, MapPin, Route, Loader2, Plus, Trash2, Check, ClipboardList, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Display-only rates. The backend is the source of truth for the charged amount.
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const INPUT_CLS = "w-full h-10 rounded-none border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const GROUPS = [
  { title: "Clothing & Accessories", emoji: "🛍️", subs: [
    { name: "Apparel", examples: "Shirts, pants, jackets, and dresses" },
    { name: "Footwear", examples: "Shoes, sneakers, and boots" },
    { name: "Accessories", examples: "Bags, belts, hats, and scarves" },
  ] },
  { title: "Gifts & Personal Care", emoji: "🎁", subs: [
    { name: "Books & Media", examples: "Novels, textbooks, DVDs, and video games" },
    { name: "Toys & Games", examples: "Board games, plushies, and action figures" },
    { name: "Cosmetics", examples: "Sealed makeup, skincare lotions, and solid soaps (non-hazardous)" },
  ] },
  { title: "Home & Electronics", emoji: "🏠", subs: [
    { name: "Soft Furnishings", examples: "Bedding, towels, and curtains" },
    { name: "Small Gadgets", examples: "Smartphones, tablets, headphones, and chargers" },
    { name: "Kitchenware", examples: "Plastic containers, mugs, and non-sharp utensils" },
  ] },
];

const EMPTY_ADDR = { search: "", house: "", building: "", landmark: "", area: "", city: "", state: "", pincode: "", googlePincode: "", lat: null, lng: null };

const DEFAULT_DELIVERY_TYPES = [
  { key: "standard", label: "Standard Delivery", description: "Regular delivery within the estimated timeframe", multiplier: 1 },
  { key: "express", label: "Express Delivery", description: "Faster delivery with priority handling", multiplier: 1.5 },
  { key: "economic", label: "Economic Delivery", description: "Budget-friendly option with longer delivery time", multiplier: 0.8 },
];

function parsePlaceComponents(place) {
  const comps = place?.address_components || [];
  const get = (types) => {
    for (const t of types) {
      const c = comps.find((x) => x.types.includes(t));
      if (c) return c.long_name;
    }
    return "";
  };
  const loc = place?.geometry?.location;
  return {
    search: place?.formatted_address || place?.name || "",
    house: get(["subpremise", "street_number"]),
    building: get(["premise", "route"]),
    landmark: "",
    area: get(["sublocality_level_1", "sublocality", "neighborhood"]),
    city: get(["locality", "administrative_area_level_2", "postal_town"]),
    state: get(["administrative_area_level_1"]),
    pincode: get(["postal_code"]),
    lat: loc ? loc.lat() : null,
    lng: loc ? loc.lng() : null,
  };
}

function reverseGeocodePincode(lat, lng) {
  return new Promise((resolve) => {
    if (lat == null || lng == null || !window.google?.maps) return resolve("");
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results) {
        for (const r of results) {
          const c = (r.address_components || []).find((x) => x.types.includes("postal_code"));
          if (c) return resolve(c.long_name);
        }
      }
      resolve("");
    });
  });
}

const addrComplete = (a) => ["house", "building", "area", "city", "state", "pincode"].every((k) => String(a[k] || "").trim());
const composeAddress = (a) => [a.house, a.building, a.landmark ? `Near ${a.landmark}` : "", a.area].filter(Boolean).join(", ");

function loadRazorpay() {
  return Promise.resolve(false);
}

export default function PickupDropFlow({ onBack }) {
  const [stage, setStage] = useState("location"); // location | parcel
  const [parcelStep, setParcelStep] = useState("select"); // select | details
  const [mapsReady, setMapsReady] = useState(false);
  const [manualMode, setManualMode] = useState(!MAPS_KEY);

  const [pickup, setPickup] = useState(EMPTY_ADDR);
  const [delivery, setDelivery] = useState(EMPTY_ADDR);
  const [autoDistance, setAutoDistance] = useState(null);
  const [distCalc, setDistCalc] = useState(false);
  const [manualDistance, setManualDistance] = useState("");

  const [selectedCats, setSelectedCats] = useState([]);
  const [manualItems, setManualItems] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", description: "", quantity: "1" });
  const [deliveryType, setDeliveryType] = useState("standard");
  const [deliveryTypes, setDeliveryTypes] = useState(DEFAULT_DELIVERY_TYPES);
  const [tiers, setTiers] = useState([]);

  const [lineDetails, setLineDetails] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [fNote, setFNote] = useState(EMPTY_FORWARDING_NOTE);
  const [fAck, setFAck] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", notes: "" });
  const [addrConfirmed, setAddrConfirmed] = useState(false);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);

  const pickupRef = useRef(null);
  const deliveryRef = useRef(null);
  const pickupAC = useRef(null);
  const deliveryAC = useRef(null);
  const pickupEl = useRef(null);
  const deliveryEl = useRef(null);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPickupField = (k, v) => setPickup((a) => ({ ...a, [k]: v }));
  const setDeliveryField = (k, v) => setDelivery((a) => ({ ...a, [k]: v }));
  const toggleCat = (name) => setSelectedCats((a) => (a.includes(name) ? a.filter((x) => x !== name) : [...a, name]));

  const addManualItem = () => {
    const name = manualForm.name.trim();
    const qty = Number(manualForm.quantity);
    if (!name) return toast.error("Enter an item name");
    if (!qty || qty < 1) return toast.error("Enter a valid quantity (at least 1)");
    setManualItems((a) => [...a, { id: `${Date.now()}-${a.length}`, name, description: manualForm.description.trim(), quantity: qty }]);
    setManualForm({ name: "", description: "", quantity: "1" });
    setShowManualForm(false);
    toast.success("Item added");
  };
  const removeManualItem = (i) => setManualItems((a) => a.filter((_, idx) => idx !== i));

  useEffect(() => {
    let active = true;
    if (!MAPS_KEY) { setManualMode(true); return; }
    const onAuthFail = () => { if (active) { setManualMode(true); toast.error("Address search unavailable — please type the address manually."); } };
    window.addEventListener(MAPS_AUTH_EVENT, onAuthFail);
    loadGoogleMaps()
      .then(() => { if (active) setMapsReady(true); })
      .catch(() => { if (active) { setManualMode(true); toast.message("Address search unavailable — please type the address manually."); } });
    return () => { active = false; window.removeEventListener(MAPS_AUTH_EVENT, onAuthFail); };
  }, []);

  useEffect(() => {
    if (!mapsReady || manualMode || stage !== "location") return;
    // (Re)bind whenever the input element is (re)mounted.
    const bind = (ref, acRef, elRef, setAddr) => {
      if (!ref.current || elRef.current === ref.current) return;
      detachAutocomplete(acRef.current);
      acRef.current = attachAutocomplete(ref.current, (p) => {
        if (!p?.geometry) return;
        const parsed = parsePlaceComponents(p);
        setAddr((prev) => ({ ...prev, ...parsed }));
        reverseGeocodePincode(parsed.lat, parsed.lng).then((pc) => { if (pc) setAddr((prev) => ({ ...prev, googlePincode: pc, pincode: prev.pincode || pc })); });
      }, { country: "in", types: ["geocode"] });
      elRef.current = acRef.current ? ref.current : null;
      if (!acRef.current) console.error("[GoogleMaps] Autocomplete could not be initialised on the address input.");
    };
    bind(pickupRef, pickupAC, pickupEl, setPickup);
    bind(deliveryRef, deliveryAC, deliveryEl, setDelivery);
  }, [mapsReady, manualMode, stage]);

  // Live rates from the admin Pricing Settings — re-fetched each time the parcel step opens.
  useEffect(() => {
    if (stage !== "parcel") return;
    api.get("/pickup-drop/pricing").then((r) => {
      if (Array.isArray(r.data?.delivery_types) && r.data.delivery_types.length) setDeliveryTypes(r.data.delivery_types);
      setTiers(r.data?.tiers || []);
    }).catch(() => toast.error("Couldn't load current pricing. Please refresh."));
  }, [stage, parcelStep]);

  // Auto-calculate driving distance once both places selected
  useEffect(() => {
    if (manualMode || pickup.lat == null || delivery.lat == null || !window.google?.maps) return;
    setDistCalc(true);
    const svc = new window.google.maps.DistanceMatrixService();
    svc.getDistanceMatrix({
      origins: [{ lat: pickup.lat, lng: pickup.lng }],
      destinations: [{ lat: delivery.lat, lng: delivery.lng }],
      travelMode: "DRIVING",
      unitSystem: window.google.maps.UnitSystem.METRIC,
    }, (res, status) => {
      setDistCalc(false);
      const el = status === "OK" ? res?.rows?.[0]?.elements?.[0] : null;
      if (el?.status === "OK") setAutoDistance(Math.round((el.distance.value / 1000) * 10) / 10);
      else { toast.error("Couldn't calculate distance automatically. Please enter it manually."); setManualMode(true); }
    });
  }, [pickup.lat, pickup.lng, delivery.lat, delivery.lng, manualMode]);

  const n = (v) => Number(v) || 0;
  const selectedDT = deliveryTypes.find((t) => t.key === deliveryType) || deliveryTypes[0] || { label: "Standard Delivery", multiplier: 1 };
  const selectedMult = Number(selectedDT.multiplier) || 1;
  const distanceKm = manualMode ? n(manualDistance) : (autoDistance || 0);
  const locationReady = addrComplete(pickup) && addrComplete(delivery) && distanceKm > 0;
  const PINCODE_WARN = "The pincode you entered does not match the selected location. Please verify your address.";
  const pickupMismatch = !!(pickup.googlePincode && pickup.pincode && pickup.googlePincode.trim() !== pickup.pincode.trim());
  const deliveryMismatch = !!(delivery.googlePincode && delivery.pincode && delivery.googlePincode.trim() !== delivery.pincode.trim());
  const anyMismatch = pickupMismatch || deliveryMismatch;
  const canProceed = locationReady && (!anyMismatch || addrConfirmed);

  const r2 = (v) => Math.round(v * 100) / 100;
  const parcelLines = [
    ...selectedCats.map((c) => ({ key: `cat:${c}`, name: c, kind: "category", description: "", defArticles: "1" })),
    ...manualItems.map((m) => ({ key: `m:${m.id}`, name: m.name, kind: "manual", description: m.description || "", defArticles: String(m.quantity || 1) })),
  ];
  const lineCalcs = parcelLines.map((l, idx) => {
    const d = { articles: l.defArticles, actual_weight_kg: "", value_inr: "", length_cm: "", width_cm: "", height_cm: "", ...(lineDetails[l.key] || {}) };
    const articles = Math.floor(n(d.articles));
    const volPer = r2((n(d.length_cm) * n(d.width_cm) * n(d.height_cm)) / 5000);
    const volTotal = r2(volPer * articles);
    const actual = r2(n(d.actual_weight_kg));
    const complete = articles >= 1 && actual > 0 && n(d.length_cm) > 0 && n(d.width_cm) > 0 && n(d.height_cm) > 0;
    return { ...l, idx, d, articles, actual, value: n(d.value_inr), volPer, volTotal, chargeable: Math.max(actual, volTotal), complete };
  });
  const grandChargeable = r2(lineCalcs.reduce((s, l) => s + l.chargeable, 0));
  const cost = computeCost(tiers, grandChargeable, distanceKm, selectedMult);
  const estCost = cost.estCost;
  const detailsReady = lineCalcs.length > 0 && lineCalcs.every((l) => l.complete) && !!cost.tier;
  const setLineField = (key, k, v) => setLineDetails((d) => ({ ...d, [key]: { ...(d[key] || {}), [k]: v } }));
  const hasParcel = selectedCats.length > 0 || manualItems.length > 0;
  const fmtInr = (v) => v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const goToPayment = () => {
    if (!locationReady) return toast.error("Complete both pickup & delivery addresses and distance");
    if (!hasParcel) return toast.error("Select at least one category or add an item");
    if (!detailsReady) return toast.error("Enter articles, actual weight and dimensions for every item");
    if (!form.customer_name.trim() || !form.customer_phone.trim()) return toast.error("Name and phone are required");
    setParcelStep("forwarding");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmAndPay = async () => {
    setPaying(true);
    try {
      const payload = {
        subcategory: selectedCats[0] || (manualItems[0] && manualItems[0].name) || "Parcel",
        subcategories: selectedCats,
        manual_items: manualItems,
        delivery_type: deliveryType,
        distance_km: distanceKm,
        parcel_lines: lineCalcs.map((l) => ({ name: l.name, kind: l.kind, description: l.description, articles: l.articles, actual_weight_kg: l.actual, value_inr: l.value, length_cm: n(l.d.length_cm), width_cm: n(l.d.width_cm), height_cm: n(l.d.height_cm) })),
        forwarding_note: { ...fNote, acknowledged: fAck },
        customer_name: form.customer_name, customer_phone: form.customer_phone, customer_email: form.customer_email, notes: form.notes,
        pickup_address: composeAddress(pickup), pickup_city: pickup.city, pickup_state: pickup.state, pickup_pincode: pickup.pincode,
        delivery_address: composeAddress(delivery), delivery_city: delivery.city, delivery_state: delivery.state, delivery_pincode: delivery.pincode,
      };
      const { data } = await api.post("/pickup-drop/book", payload);
      setDone(data);
      toast.success("Payment successful");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPaying(false);
    }
  };

  if (done) {
    const addrLine = (a) => a ? [a.address, a.city, a.state, a.pincode].filter(Boolean).join(", ") : "";
    return (
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-xl border border-border bg-card p-10" data-testid="pd-confirmation">
          <div className="text-center">
            <div className="h-14 w-14 bg-emerald-500/15 text-emerald-500 grid place-items-center mx-auto mb-6"><CheckCircle2 className="h-8 w-8" /></div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Booking Confirmed</h1>
            <p className="text-muted-foreground mt-2">Your pickup has been booked and saved. We'll be in touch to schedule collection.</p>
          </div>
          <div className="mt-6 border border-border bg-muted p-4 text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Booking ID</div>
            <div className="font-mono-num text-2xl font-bold text-primary mt-1" data-testid="pd-booking-id">{done.booking_id}</div>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="border border-border p-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Pickup</div>
              <div data-testid="pd-conf-pickup">{addrLine(done.pickup)}</div>
            </div>
            <div className="border border-border p-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Delivery</div>
              <div data-testid="pd-conf-delivery">{addrLine(done.delivery)}</div>
            </div>
          </div>
          <div className="mt-4 border border-border p-4 flex items-center gap-3">
            <Route className="h-5 w-5 text-primary shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Estimated Delivery</div>
              <div className="font-medium" data-testid="pd-conf-eta">By {done.estimated_delivery_date} · approx {done.estimated_delivery_days} days · ₹{done.cost?.toLocaleString("en-IN")}</div>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <Button asChild className="flex-1 rounded-none" data-testid="pd-track"><Link to={`/pickup-track/${done.track_token}`}>Track your order</Link></Button>
            <Button variant="outline" className="flex-1 rounded-none" onClick={onBack} data-testid="pd-new">Done</Button>
          </div>
        </div>
      </div>
    );
  }

  const backHandler = () => {
    if (stage === "parcel" && parcelStep === "payment") return setParcelStep("forwarding");
    if (stage === "parcel" && parcelStep === "forwarding") return setParcelStep("details");
    if (stage === "parcel" && parcelStep === "details") return setParcelStep("select");
    if (stage === "parcel") return setStage("location");
    return onBack();
  };
  const backLabel = stage === "parcel" && parcelStep === "payment" ? "Back to forwarding note" : stage === "parcel" && parcelStep === "forwarding" ? "Back to details" : stage === "parcel" && parcelStep === "details" ? "Back to items" : stage === "parcel" ? "Back to locations" : "Back to services";
  const routeText = (a) => [composeAddress(a), a.city, a.state, a.pincode].filter(Boolean).join(", ");

  return (
    <div className="w-full min-w-0 max-w-3xl mx-auto px-6 py-10">
      <button type="button" onClick={backHandler} data-testid="pd-back" className="mb-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors"><ArrowLeft className="h-4 w-4" /> {backLabel}</button>
      <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-2"><Package className="h-4 w-4" /> Pickup & Drop</div>
      <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-[0.95]">Send a parcel.</h1>

      {stage === "location" ? (
        <>
          <p className="text-muted-foreground mt-3">Search your pickup and delivery addresses, then confirm the details. We'll work out the distance for you.</p>

          <AddressBlock
            title="Pickup Location" testid="pickup" addr={pickup} onField={setPickupField}
            manualMode={manualMode} inputRef={pickupRef} pincodeWarning={pickupMismatch ? PINCODE_WARN : null} />

          <AddressBlock
            title="Delivery Location" testid="delivery" addr={delivery} onField={setDeliveryField}
            manualMode={manualMode} inputRef={deliveryRef} pincodeWarning={deliveryMismatch ? PINCODE_WARN : null} />

          <section className="border border-border bg-card p-6 mt-6 space-y-3">
            {!manualMode ? (
              <div className="border border-border bg-muted p-4 flex items-center gap-2" data-testid="pd-distance">
                <Route className="h-4 w-4 text-primary" />
                {distCalc ? (
                  <span className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculating distance…</span>
                ) : autoDistance != null ? (
                  <span className="text-sm font-bold">Distance: <span className="font-mono-num text-primary">{autoDistance} km</span></span>
                ) : (
                  <span className="text-sm text-muted-foreground">Select both locations to calculate the distance.</span>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="pd-manual-distance">Distance (km)</Label>
                <Input id="pd-manual-distance" type="number" min="0" value={manualDistance} onChange={(e) => setManualDistance(e.target.value)} className="rounded-none font-mono-num" placeholder="e.g. 42" data-testid="pd-manual-distance" />
              </div>
            )}
            {MAPS_KEY && (
              <button type="button" onClick={() => setManualMode((m) => !m)} className="text-xs text-muted-foreground hover:text-primary underline" data-testid="pd-toggle-mode">
                {manualMode ? "Use address search instead" : "Can't find your location? Enter manually"}
              </button>
            )}
            {anyMismatch && (
              <label className="flex items-start gap-2 cursor-pointer border border-amber-500/40 bg-amber-500/5 p-3" data-testid="pd-confirm-addr-label">
                <Checkbox checked={addrConfirmed} onCheckedChange={(v) => setAddrConfirmed(!!v)} className="mt-0.5 rounded-none" data-testid="pd-confirm-addr" />
                <span className="text-sm text-muted-foreground">I confirm my address is correct.</span>
              </label>
            )}
            <div className="flex justify-end pt-1">
              <Button type="button" disabled={!canProceed} onClick={() => setStage("parcel")} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="pd-location-continue">
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </section>
        </>
      ) : (
        <>
          <div className="mt-3 border border-border bg-muted p-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="pd-route-summary">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium">{pickup.city || "Pickup"}</span>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium">{delivery.city || "Delivery"}</span>
            <span className="text-muted-foreground">· Distance: <span className="font-mono-num text-primary font-bold">{distanceKm} km</span></span>
          </div>

          {parcelStep === "select" ? (
            <>
              <p className="text-muted-foreground mt-6">Select one or more Regular Parcel categories. You can also add your own items.</p>
              <div className="mt-4 space-y-6" data-testid="pd-subcategory-select">
                {GROUPS.map((g) => (
                  <section key={g.title} className="border border-border bg-card p-6">
                    <div className="font-heading font-bold text-lg tracking-tight mb-4">{g.emoji} {g.title}</div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {g.subs.map((s) => {
                        const on = selectedCats.includes(s.name);
                        return (
                          <button key={s.name} type="button" data-testid={`pd-sub-${s.name}`} aria-pressed={on} onClick={() => toggleCat(s.name)}
                            className={`relative text-left border p-4 transition-colors group ${on ? "border-primary bg-primary/10" : "border-border hover:border-primary"}`}>
                            <div className={`absolute top-3 right-3 h-5 w-5 grid place-items-center border ${on ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>{on && <Check className="h-3.5 w-3.5" />}</div>
                            <div className={`font-bold text-sm uppercase tracking-wide pr-7 ${on ? "text-primary" : "group-hover:text-primary"} transition-colors`}>{s.name}</div>
                            <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{s.examples}</div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}

                <section className="border border-border bg-card p-6" data-testid="pd-manual-items">
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-bold text-lg tracking-tight">Add Item Manually</div>
                    {!showManualForm && (
                      <Button type="button" variant="outline" size="sm" className="rounded-none gap-1.5" onClick={() => setShowManualForm(true)} data-testid="pd-add-item-btn"><Plus className="h-4 w-4" /> Add Item</Button>
                    )}
                  </div>

                  {showManualForm && (
                    <div className="mt-4 border border-dashed border-primary/60 p-4 space-y-3" data-testid="pd-manual-form">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Item Name *</Label>
                          <Input className="rounded-none" value={manualForm.name} placeholder="e.g. Table lamp" onChange={(e) => setManualForm((m) => ({ ...m, name: e.target.value }))} data-testid="pd-item-name" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManualItem(); } }} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Quantity *</Label>
                          <Input type="number" min="1" className="rounded-none font-mono-num" value={manualForm.quantity} onChange={(e) => setManualForm((m) => ({ ...m, quantity: e.target.value }))} data-testid="pd-item-qty" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Item Description (optional)</Label>
                        <Input className="rounded-none" value={manualForm.description} placeholder="Any details about the item" onChange={(e) => setManualForm((m) => ({ ...m, description: e.target.value }))} data-testid="pd-item-desc" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="ghost" className="rounded-none" onClick={() => { setShowManualForm(false); setManualForm({ name: "", description: "", quantity: "1" }); }}>Cancel</Button>
                        <Button type="button" className="rounded-none gap-1.5 font-bold uppercase" onClick={addManualItem} data-testid="pd-item-add"><Plus className="h-4 w-4" /> Add</Button>
                      </div>
                    </div>
                  )}

                  {manualItems.length > 0 && (
                    <div className="mt-4 border border-border divide-y divide-border" data-testid="pd-manual-list">
                      {manualItems.map((it, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2" data-testid={`pd-manual-item-${i}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{it.name} <span className="text-muted-foreground font-mono-num">× {it.quantity}</span></div>
                            {it.description && <div className="text-xs text-muted-foreground truncate">{it.description}</div>}
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="rounded-none h-8 w-8 text-destructive shrink-0" onClick={() => removeManualItem(i)} data-testid={`pd-remove-item-${i}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">{selectedCats.length} categor{selectedCats.length === 1 ? "y" : "ies"} · {manualItems.length} item{manualItems.length === 1 ? "" : "s"}</div>
                <Button type="button" disabled={!hasParcel} onClick={() => setParcelStep("details")} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="pd-items-continue">Continue <ArrowRight className="h-4 w-4" /></Button>
              </div>
            </>
          ) : parcelStep === "details" ? (
            <>
              <div className="mt-4 space-y-3" data-testid="pd-parcel-lines">
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2"><Ruler className="h-5 w-5 text-primary" /> Item Details</h2>
                <p className="text-sm text-muted-foreground">Enter details for each category or item. Dimensions are per article, in centimetres.</p>
                {lineCalcs.map((l) => (
                  <ParcelLineCard key={l.key} line={l} open={!collapsed[l.key]}
                    onToggle={() => setCollapsed((c) => ({ ...c, [l.key]: !c[l.key] }))}
                    onField={(k, v) => setLineField(l.key, k, v)} />
                ))}
              </div>

              <section className="border border-border bg-card p-6 mt-6">
                <div data-testid="pd-delivery-type">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Delivery Type</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {deliveryTypes.map((t) => {
                      const on = t.key === deliveryType;
                      return (
                        <button key={t.key} type="button" data-testid={`pd-dtype-${t.key}`} aria-pressed={on} onClick={() => setDeliveryType(t.key)}
                          className={`relative text-left border p-4 transition-colors ${on ? "border-primary bg-primary/10" : "border-border hover:border-primary"}`}>
                          <div className={`absolute top-3 right-3 h-5 w-5 grid place-items-center border ${on ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>{on && <Check className="h-3.5 w-3.5" />}</div>
                          <div className={`font-bold text-sm uppercase tracking-wide pr-7 ${on ? "text-primary" : ""}`}>{t.label}</div>
                          <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{t.description}</div>
                          <div className="text-[11px] font-mono-num mt-2 text-primary">{Number(t.multiplier)}× base rate</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

              </section>

              <section className="border border-border bg-card p-6 space-y-3 mt-6">
                <h3 className="font-heading font-bold uppercase tracking-tight">Your contact</h3>
                <div className="grid md:grid-cols-3 gap-3">
                  <Field label="Full name" v={form.customer_name} on={(v) => setF("customer_name", v)} id="pd_name" />
                  <Field label="Phone" v={form.customer_phone} on={(v) => setF("customer_phone", v)} id="pd_phone" />
                  <Field label="Email (optional)" v={form.customer_email} on={(v) => setF("customer_email", v)} id="pd_email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pd_notes">Notes (optional)</Label>
                  <Textarea id="pd_notes" value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="rounded-none" rows={2} data-testid="field-pd_notes" />
                </div>
              </section>

              <section className="border border-border bg-card p-6 mt-6" data-testid="pd-order-summary">
                <h3 className="font-heading font-bold uppercase tracking-tight flex items-center gap-2 mb-4"><ClipboardList className="h-5 w-5 text-primary" /> Order Summary</h3>
                <dl className="space-y-3 text-sm">
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                    <SummaryRow label="Pickup" value={routeText(pickup)} />
                    <SummaryRow label="Delivery" value={routeText(delivery)} />
                  </div>
                </dl>
                <ParcelSummary lines={lineCalcs} grand={grandChargeable} distanceKm={distanceKm} cost={cost} deliveryLabel={selectedDT.label} mult={selectedMult} fmtInr={fmtInr} />
              </section>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground" data-testid="pd-payable-now">Payable now: <span className="font-mono-num font-bold text-foreground text-lg">₹{fmtInr(estCost)}</span></div>
                <Button type="button" disabled={!detailsReady} onClick={goToPayment} className="rounded-none gap-2 font-bold uppercase tracking-wide w-full sm:w-auto" data-testid="pd-proceed-payment">
                  Continue to Forwarding Note <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : parcelStep === "forwarding" ? (
            <ForwardingNote note={fNote} onField={(k, v) => setFNote((f) => ({ ...f, [k]: v }))} ack={fAck} onAck={setFAck}
              onProceed={() => { if (!fAck) return; setParcelStep("payment"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          ) : (
            <MockPayment
              pickup={pickup} delivery={delivery} lines={lineCalcs} grand={grandChargeable} cost={cost}
              distanceKm={distanceKm} deliveryLabel={selectedDT.label} deliveryMult={selectedMult}
              fmtInr={fmtInr} paying={paying} onConfirm={confirmAndPay} routeText={routeText} />
          )}
        </>
      )}
    </div>
  );
}

function AddressBlock({ title, testid, addr, onField, manualMode, inputRef, pincodeWarning }) {
  return (
    <section className="border border-border bg-card p-6 mt-6 space-y-4" data-testid={`pd-${testid}-block`}>
      <h2 className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> {title}</h2>

      {!manualMode && (
        <div className="space-y-2">
          <Label>Search address</Label>
          <input ref={inputRef} type="text" className={INPUT_CLS} placeholder={`Start typing a ${testid} address…`} data-testid={`pd-${testid}-autocomplete`} />
          <p className="text-[11px] text-muted-foreground">Pick a suggestion, then confirm the details below.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid={`pd-${testid}-fields`}>
        <AddrField label="House / Flat / Shop No." required v={addr.house} on={(v) => onField("house", v)} testid={`pd-${testid}-house`} />
        <AddrField label="Building / Society / Street Name" required v={addr.building} on={(v) => onField("building", v)} testid={`pd-${testid}-building`} />
        <AddrField label="Landmark (optional)" v={addr.landmark} on={(v) => onField("landmark", v)} testid={`pd-${testid}-landmark`} />
        <AddrField label="Area / Locality" required v={addr.area} on={(v) => onField("area", v)} testid={`pd-${testid}-area`} />
        <AddrField label="City" required v={addr.city} on={(v) => onField("city", v)} testid={`pd-${testid}-city`} />
        <AddrField label="State" required v={addr.state} on={(v) => onField("state", v)} testid={`pd-${testid}-state`} />
        <AddrField label="Pincode" required v={addr.pincode} on={(v) => onField("pincode", v)} testid={`pd-${testid}-pincode`} warning={pincodeWarning} />
      </div>
    </section>
  );
}

function AddrField({ label, required, v, on, testid, warning }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}{required && <span className="text-primary"> *</span>}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} className={`rounded-none ${warning ? "border-amber-500 focus-visible:ring-amber-500" : ""}`} data-testid={testid} />
      {warning && <p className="text-[11px] text-amber-600 dark:text-amber-500" data-testid={`${testid}-warning`}>{warning}</p>}
    </div>
  );
}

function MockPayment({ pickup, delivery, lines, grand, cost, distanceKm, deliveryLabel, deliveryMult, fmtInr, paying, onConfirm, routeText }) {
  const estCost = cost.estCost;
  return (
    <div data-testid="pd-mock-payment">
      <section className="border border-border bg-card p-6 mt-4" data-testid="pd-payment-summary">
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2 mb-3"><CreditCard className="h-5 w-5 text-primary" /> Review & Pay</h2>
        <div className="border border-dashed border-primary/40 bg-primary/5 p-3 mb-4 text-xs text-muted-foreground">Mock payment for testing — no real gateway or charge is involved.</div>
        <dl className="space-y-3 text-sm">
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
            <SummaryRow label="Pickup" value={routeText(pickup)} />
            <SummaryRow label="Delivery" value={routeText(delivery)} />
          </div>
        </dl>
        <ParcelSummary lines={lines} grand={grand} distanceKm={distanceKm} cost={cost} deliveryLabel={deliveryLabel} mult={deliveryMult} fmtInr={fmtInr} />
      </section>
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">Total: <span className="font-mono-num font-bold text-foreground text-lg">₹{fmtInr(estCost)}</span></div>
        <Button type="button" disabled={paying} onClick={onConfirm} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="pd-confirm-pay">
          {paying ? "Processing…" : <>Confirm &amp; Pay <CreditCard className="h-4 w-4" /></>}
        </Button>
      </div>
    </div>
  );
}

function ParcelLineCard({ line, open, onToggle, onField }) {
  const d = line.d;
  const t = `pd-line-${line.idx}`;
  return (
    <section className="border border-border bg-card" data-testid={t}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/50 transition-colors" data-testid={`${t}-toggle`} aria-expanded={open}>
        <div className={`h-6 w-6 grid place-items-center border shrink-0 ${line.complete ? "bg-emerald-500/15 border-emerald-500 text-emerald-500" : "border-border text-muted-foreground"}`}>
          {line.complete ? <Check className="h-3.5 w-3.5" /> : <Package className="h-3.5 w-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm uppercase tracking-wide truncate">{line.name}</div>
          <div className="text-[11px] text-muted-foreground" data-testid={`${t}-status`}>
            {line.kind === "manual" ? "Added item" : "Category"} · {line.complete ? `${line.articles} article${line.articles === 1 ? "" : "s"} · ${line.chargeable.toFixed(2)} kg chargeable` : "details needed"}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-border p-4 space-y-4" data-testid={`${t}-body`}>
          <div className="grid sm:grid-cols-3 gap-3">
            <LineNum label="Number of Articles" step="1" v={d.articles} on={(v) => onField("articles", v)} testid={`${t}-articles`} />
            <LineNum label="Actual Weight (kg, total)" v={d.actual_weight_kg} on={(v) => onField("actual_weight_kg", v)} testid={`${t}-weight`} />
            <LineNum label="Value of Item (₹)" v={d.value_inr} on={(v) => onField("value_inr", v)} testid={`${t}-value`} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Package Dimensions per Article</div>
            <div className="grid grid-cols-3 gap-3">
              <LineNum label="Length (cm)" v={d.length_cm} on={(v) => onField("length_cm", v)} testid={`${t}-length`} />
              <LineNum label="Width (cm)" v={d.width_cm} on={(v) => onField("width_cm", v)} testid={`${t}-width`} />
              <LineNum label="Height (cm)" v={d.height_cm} on={(v) => onField("height_cm", v)} testid={`${t}-height`} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 border border-border bg-muted p-3">
            <Metric label="Vol. wt / article" value={`${line.volPer.toFixed(2)} kg`} testid={`${t}-volper`} />
            <Metric label="Total vol. wt" value={`${line.volTotal.toFixed(2)} kg`} testid={`${t}-voltotal`} />
            <Metric label="Chargeable wt" value={`${line.chargeable.toFixed(2)} kg`} testid={`${t}-chargeable`} strong />
          </div>
          <p className="text-[11px] text-muted-foreground">Volumetric = L × W × H ÷ 5000 per article × articles. Chargeable = higher of actual vs total volumetric weight.</p>
        </div>
      )}
    </section>
  );
}

function LineNum({ label, v, on, testid, step = "any" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" step={step} value={v} onChange={(e) => on(e.target.value)} className="rounded-none font-mono-num" data-testid={testid} />
    </div>
  );
}

function Metric({ label, value, strong, testid }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
      <div className={`font-mono-num text-sm font-bold ${strong ? "text-primary" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

// Mirrors backend _pickup_drop_quote: (km × rate/km) + (kg × rate/kg) + base, × multiplier, floored at tier minimum.
function computeCost(tiers, weight, km, mult) {
  const r2 = (v) => Math.round(v * 100) / 100;
  const tier = tiers.find((t) => weight <= t.max_weight_kg) || null;
  if (!tier) return { tier: null, overCapacity: tiers.length > 0, estCost: 0 };
  const distCost = r2(km * tier.rate_per_km);
  const weightCost = r2(weight * tier.rate_per_kg);
  const subtotal = r2(distCost + weightCost + tier.base_price);
  const calculated = r2(subtotal * mult);
  const minApplied = tier.min_order_value > calculated;
  return { tier, distCost, weightCost, subtotal, calculated, minApplied, estCost: minApplied ? tier.min_order_value : calculated };
}

function ParcelSummary({ lines, grand, distanceKm, cost, deliveryLabel, mult, fmtInr }) {
  const kg = (v) => `${v.toFixed(2)} kg`;
  return (
    <div className="mt-4 pt-4 border-t border-border space-y-4" data-testid="pd-parcel-summary">
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-2">Item</th><th className="text-right p-2">Articles</th><th className="text-right p-2">Actual</th><th className="text-right p-2">Volumetric</th><th className="text-right p-2">Chargeable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border font-mono-num">
            {lines.map((l) => (
              <tr key={l.key} data-testid={`pd-summary-row-${l.idx}`}>
                <td className="p-2 font-sans font-medium">{l.name}</td>
                <td className="p-2 text-right">{l.articles}</td>
                <td className="p-2 text-right whitespace-nowrap">{kg(l.actual)}</td>
                <td className="p-2 text-right whitespace-nowrap">{kg(l.volTotal)}</td>
                <td className="p-2 text-right whitespace-nowrap font-bold">{kg(l.chargeable)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-primary bg-primary/5">
              <td colSpan={4} className="p-2 text-[11px] uppercase tracking-widest font-bold">Grand Total Chargeable Weight</td>
              <td className="p-2 text-right font-mono-num font-bold text-primary whitespace-nowrap" data-testid="pd-grand-chargeable">{kg(grand)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <dl className="text-sm space-y-1.5" data-testid="pd-cost-breakdown">
        {!cost.tier ? (
          <div className="border border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400 p-3 text-xs" data-testid="pd-over-capacity">
            {cost.overCapacity ? `Chargeable weight ${grand.toFixed(2)} kg exceeds our largest vehicle. Please contact us for a custom quote.` : "Loading current rates…"}
          </div>
        ) : (
          <>
            <CostRow label="Truck Load Type" value={cost.tier.label} testid="pd-summary-tier" />
            <CostRow label={`Distance: ${distanceKm} km × ₹${fmtInr(cost.tier.rate_per_km)}/km`} value={`₹${fmtInr(cost.distCost)}`} testid="pd-summary-distance" />
            <CostRow label={`Chargeable Weight: ${grand.toFixed(2)} kg × ₹${fmtInr(cost.tier.rate_per_kg)}/kg`} value={`₹${fmtInr(cost.weightCost)}`} testid="pd-summary-weight" />
            <CostRow label="Base Price" value={`₹${fmtInr(cost.tier.base_price)}`} testid="pd-summary-base" />
            <div className="border-t border-border pt-1.5"><CostRow label="Subtotal" value={`₹${fmtInr(cost.subtotal)}`} testid="pd-summary-subtotal" /></div>
            <CostRow label={`Delivery Type (${deliveryLabel.replace(/ Delivery$/, "")})`} value={`× ${mult}`} testid="pd-summary-multiplier" />
            {cost.minApplied && (
              <div className="flex justify-between gap-3 text-amber-600 dark:text-amber-400" data-testid="pd-summary-min-applied">
                <dt>Minimum order value applied</dt><dd className="font-mono-num whitespace-nowrap">₹{fmtInr(cost.tier.min_order_value)}</dd>
              </div>
            )}
          </>
        )}
        <div className="flex justify-between items-baseline gap-3 pt-2 border-t border-border">
          <dt className="font-bold uppercase tracking-wide text-xs">Final Payable Amount</dt>
          <dd className="font-mono-num text-xl font-bold text-primary" data-testid="pd-est-cost">₹{fmtInr(cost.estCost)}</dd>
        </div>
      </dl>
    </div>
  );
}

function CostRow({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground min-w-0">{label}</dt>
      <dd className="font-mono-num text-right shrink-0 max-w-[60%]" data-testid={testid}>{value}</dd>
    </div>
  );
}

function SummaryRow({ label, value, strong }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`${strong ? "text-primary font-bold text-base font-mono-num" : "font-medium"}`}>{value || "—"}</div>
    </div>
  );
}

function Field({ label, v, on, id }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={v} onChange={(e) => on(e.target.value)} className="rounded-none" data-testid={`field-${id}`} />
    </div>
  );
}

